// @flow

import * as React from 'react';
import { connect } from 'react-redux';

import classNames from '@ta/common.classnames';
import { featureIsEnabled } from '@ta/common.features';
import { localize, unescaped } from '@ta/common.i18n';
import { Link } from '@ta/common.routing';
import { InteractionLogger } from '@ta/tracking.interactions';
import * as TripTypes from '@ta/trips.trip-types';

import LinkToTrip from './LinkToTrip';
import * as Types from './types';
import styles from './UGCShelfItemInfo.less';

type ExternalProps = {|
  /** The Trip object */
  trip: TripTypes.Trip,

  /** Member object of the owner of the UGC */
  member: Types.Member,

  /** List of locations tagged on the UGC */
  locations: Types.Location[],

  /** The 'to' object for the tagged locations string Link */
  taggedPoisLinkTo: any,

  /** A function to execute on the click of the taggedPoisLink */
  onClick?: (SyntheticMouseEvent<HTMLElement>) => mixed,
|};

type Props = {
  ...ExternalProps,
  page: string,
};

const UGCShelfItemInfo = (props: Props) => {
  const { trip, member, locations, taggedPoisLinkTo, onClick, page } = props;
  const curatedTrips =
    featureIsEnabled('curated_trips_attractions') || featureIsEnabled('curated_trips_restaurants');

  let target;
  let localizedFeaturedType;
  switch (page) {
    case 'Restaurants':
    case 'MobileRestaurants':
      target = '_blank';
      localizedFeaturedType = unescaped(
        localize('Featuring_n_eateries_plural', {
          '0': 'bold',
          '1': locations?.length || '',
        }),
      ); // Featuring: <span class="{0}">{1} {1, plural, one{eatery} other{eateries}}</span>
      break;
    case 'Attractions':
    case 'MobileAttractions':
      target = '_blank';
      localizedFeaturedType = unescaped(
        localize('trips_home_featuring_count_v2', {
          '0': locations?.length || '',
        }),
      ); // Featuring: <span>{0, plural, one{# place} other{# places}}</span>
      break;
    default:
      target = '_self';
      localizedFeaturedType = unescaped(
        localize('trips_home_featuring_count_v2', {
          '0': locations?.length || '',
        }),
      ); // Featuring: <span>{0, plural, one{# place} other{# places}}</span>
  }

  const MIN_OBJECTS = 2;

  return (
    <React.Fragment>
      {locations?.length > MIN_OBJECTS && (
        <>
          {curatedTrips ? (
            <LinkToTrip trip={trip}>
              <span className={classNames('ui_link', styles.tagged_pois_container)}>
                <span className={styles.tagged_pois}>{localizedFeaturedType}</span>
              </span>
            </LinkToTrip>
          ) : (
            <Link
              to={taggedPoisLinkTo}
              onClick={onClick}
              className={classNames('ui_link', styles.tagged_pois_container)}
              target={target}
            >
              <span className={styles.tagged_pois}>{localizedFeaturedType}</span>
            </Link>
          )}
        </>
      )}
      <div className={styles.name_likes_container}>
        <InteractionLogger>
          {logInteraction => (
            <Link
              href={member?.route?.url}
              onClick={e => logInteraction(e, 'userClick', { userId: member?.id })}
              target={target}
            >
              <span className={styles.display_name_container}>
                <span
                  className={classNames(styles.display_name, {
                    [`${styles.verified} verified`]: member?.isVerified,
                  })}
                >
                  {member?.displayName}
                </span>
              </span>
            </Link>
          )}
        </InteractionLogger>
      </div>
    </React.Fragment>
  );
};

UGCShelfItemInfo.defaultProps = { onClick: undefined };

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  page: state.route.page,
}))(UGCShelfItemInfo);
